import { Trans } from "@lingui/macro";
import { useMediaQuery, useTheme } from "@mui/material";
import { useIsomorphicLayoutEffect } from "@react-hookz/web";
import {
  UiBox,
  UiButton,
  UiButtonBase,
  UiDialogResponsive,
  UiLink,
  UiModuleLoader,
  UiSvgIcon,
  UiTooltip,
  UiTypography,
} from "@smp/ui";
import { IconPlus, IconShare } from "@tabler/icons-react";
import { FC, useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { UnsplashCard } from "@/components/features/polotno_templates/components";
import { useContextPolotnoTemplates } from "@/components/features/polotno_templates/context";
import { useTemplateVirtualizer } from "@/components/features/polotno_templates/hooks";
import { useQueryPolotnoUnsplash } from "@/queries/polotno";
import { customGeneratePath } from "@/utils";

import { useStyles } from "./unsplash_dialog.css";

export const UnsplashDialog: FC<{
  enablePortal?: boolean;
}> = ({ enablePortal }) => {
  const classes = useStyles();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    actions: { openInEditor, copyToEditor, setActiveEntity },
    state: { activeUnsplashEntry, pathPattern, isRoutingDisabled },
  } = useContextPolotnoTemplates();
  const parentRef = useRef<HTMLDivElement | null>(null);
  const [headerRef, setHeaderRef] = useState<HTMLDivElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);
  const [openCopied, setOpenCopied] = useState(false);

  const { data: unsplashResp, isFetching: isUnsplashLoading } =
    useQueryPolotnoUnsplash(activeUnsplashEntry?.query, {
      enabled: !!activeUnsplashEntry?.query,
    });

  const firstName = activeUnsplashEntry?.user_name;
  const userLink = activeUnsplashEntry?.user_link;

  const unsplashItems = useMemo(
    () =>
      unsplashResp?.pages.flatMap(({ results }) =>
        results.map(
          (entry) =>
            ({
              key: `unsplash-${entry.id}`,
              payload: entry,
              type: "unsplash",
            } as Parameters<typeof useTemplateVirtualizer>[0][number]),
        ),
      ) || [],
    [unsplashResp?.pages],
  );

  const { virtualizer, calculateStyles, getTotalSizeCustom } =
    useTemplateVirtualizer(unsplashItems, parentRef.current, {
      scrollMargin,
    });

  const handleClose = useCallback(() => {
    setActiveEntity(undefined);

    if (!isRoutingDisabled) {
      navigate(customGeneratePath(pathPattern), {
        replace: true,
      });
    }
  }, [isRoutingDisabled, navigate, pathPattern, setActiveEntity]);

  const handleEdit = useCallback(
    (e: MouseEvent) => {
      setActiveEntity(undefined);

      if (activeUnsplashEntry) {
        openInEditor(activeUnsplashEntry, e.ctrlKey);
      }
    },
    [activeUnsplashEntry, openInEditor, setActiveEntity],
  );

  const handleShare = useCallback(() => {
    if (activeUnsplashEntry) {
      setOpenCopied(true);
      navigator.clipboard.writeText(window.location.href);
    }
  }, [activeUnsplashEntry]);

  useIsomorphicLayoutEffect(() => {
    if (headerRef) {
      setScrollMargin(headerRef?.clientHeight);

      const resizeObserver = new ResizeObserver(() => {
        setScrollMargin(headerRef?.clientHeight || 0);
      });

      resizeObserver.observe(headerRef);
    }
  }, [headerRef]);

  return (
    <UiDialogResponsive
      open={Boolean(activeUnsplashEntry)}
      fullWidth
      disablePortal={enablePortal !== true}
      onClose={handleClose}
      className={classes.dialog}
      classes={{
        dialogContent: classes.dialog__content,
        dialogTitle: classes.dialog__title,
        paper: classes.dialog__paper,
      }}
    >
      {isUnsplashLoading ? (
        <UiModuleLoader loadingText={<Trans>Loading</Trans>} />
      ) : (
        <UiBox className={classes.container} ref={parentRef}>
          <UiBox
            ref={setHeaderRef}
            className={classes.header}
            style={{ marginBottom: scrollMargin * -1 }}
          >
            <UiBox
              display="flex"
              mb={6}
              flexDirection={isMobile ? "column" : "row"}
              columnGap={6}
              rowGap={2}
            >
              <UiBox mr={6} className={classes.preview}>
                <img
                  src={activeUnsplashEntry?.url}
                  alt=""
                  className={classes.preview__image}
                />
              </UiBox>

              <UiBox className={classes.info}>
                <UiTypography variant="h3" gutterBottom gutterBottomSize="lg">
                  <Trans>Photo</Trans>
                </UiTypography>

                <UiBox className={classes.info__user}>
                  <UiBox className={classes.info__user__icn}>
                    <UiSvgIcon viewBox="0 0 12 12" fontSize="inherit">
                      <path d="M3.75 3.375V0H8.25V3.375H3.75ZM8.25 5.25H12V12H0V5.25H3.75V8.625H8.25V5.25Z" />
                    </UiSvgIcon>
                  </UiBox>

                  <UiTypography>
                    {firstName && userLink ? (
                      <Trans>
                        Photo by{" "}
                        <UiLink
                          target="_blank"
                          href={userLink}
                          underline="always"
                        >
                          {firstName}
                        </UiLink>{" "}
                        on{" "}
                        <UiLink
                          target="_blank"
                          href="https://unsplash.com"
                          underline="always"
                        >
                          Unsplash
                        </UiLink>
                      </Trans>
                    ) : (
                      <Trans>
                        Photo by{" "}
                        <UiLink
                          target="_blank"
                          href="https://unsplash.com"
                          underline="always"
                        >
                          Unsplash
                        </UiLink>
                      </Trans>
                    )}
                  </UiTypography>
                </UiBox>

                <UiBox mt={3} mb={2} display="flex">
                  <UiButton
                    variant="contained"
                    color="primary"
                    className={classes.editButton}
                    onClick={handleEdit}
                  >
                    <Trans>templates.modal.button_Edit template</Trans>
                  </UiButton>
                  <UiTooltip
                    PopperProps={{
                      disablePortal: true,
                    }}
                    leaveDelay={500}
                    title={
                      openCopied ? (
                        <Trans>Copied to clipboard</Trans>
                      ) : (
                        <Trans>Share</Trans>
                      )
                    }
                  >
                    <UiButtonBase
                      children={<IconShare />}
                      onClick={handleShare}
                      className={classes.action_button}
                    />
                  </UiTooltip>
                </UiBox>

                {copyToEditor && activeUnsplashEntry && (
                  <UiBox mb={2} display="flex" alignItems="center">
                    <UiButton
                      variant="semifilled"
                      onClick={() => {
                        setActiveEntity(undefined);
                        copyToEditor(activeUnsplashEntry);
                      }}
                      startIcon={<IconPlus />}
                    >
                      <Trans>templates.modal.button_Copy template</Trans>
                    </UiButton>
                  </UiBox>
                )}
              </UiBox>
            </UiBox>

            {!!unsplashItems.length && (
              <UiBox pb={3}>
                <UiTypography variant="h5">
                  <Trans>You might also like</Trans>
                </UiTypography>
              </UiBox>
            )}
          </UiBox>

          {parentRef.current && (
            <UiBox
              style={{
                height: `${Math.max(0, getTotalSizeCustom())}px`,
                position: "relative",
                width: "100%",
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const item = unsplashItems[virtualItem.index];

                if (item.type !== "unsplash") {
                  return null;
                }

                return (
                  <UiBox
                    style={calculateStyles(virtualItem)}
                    key={virtualItem.key.toString()}
                  >
                    <div
                      ref={virtualizer.measureElement}
                      data-index={virtualItem.index}
                    >
                      <UnsplashCard payload={item.payload} />
                    </div>
                  </UiBox>
                );
              })}
            </UiBox>
          )}
        </UiBox>
      )}
    </UiDialogResponsive>
  );
};
